import React, { useEffect, useRef, useState } from 'react';
import appointmentService from '../../services/appointmentService';
import SlotPicker from './SlotPicker';
import {
  dateOnly, formatDateLabel, slotLabel, todayIso,
} from '../../utils/appointmentLogic';

export const Shell = ({ title, onClose, busy, children, footer, wide = false }) => (
  <div className="apt-backdrop apt-backdrop-top" onClick={() => !busy && onClose()}>
    <div className={`apt-modal ${wide ? 'apt-modal-wide' : ''}`} onClick={(e) => e.stopPropagation()} role="dialog" aria-label={title}>
      <div className="apt-modal-head">
        <h2>{title}</h2>
        <button type="button" className="apt-icon-btn" onClick={onClose} disabled={busy} aria-label="Close">×</button>
      </div>
      <div className="apt-modal-body">{children}</div>
      <div className="apt-modal-foot">{footer}</div>
    </div>
  </div>
);

export const RescheduleModal = ({ appointment, onClose, onDone }) => {
  const [date, setDate] = useState(dateOnly(appointment.appointmentDate));
  const [slot, setSlot] = useState('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [refresh, setRefresh] = useState(0);
  const busyRef = useRef(false);
  const sameDay = date === dateOnly(appointment.appointmentDate);

  const submit = async () => {
    if (busyRef.current) return;
    if (!date || !slot) { setError('Choose the new date and slot.'); return; }
    busyRef.current = true; setBusy(true); setError('');
    try {
      const res = await appointmentService.reschedule(appointment.appointmentId, {
        appointmentDate: date, slotStart: slot, reason: reason.trim() || undefined, expectedVersion: appointment.version,
      });
      if (res.success) onDone(res.data); else setError(res.message || 'Could not reschedule.');
    } catch (err) {
      setError(err.response?.data?.message || 'Could not reschedule.');
      setSlot(''); setRefresh((n) => n + 1);
    } finally { busyRef.current = false; setBusy(false); }
  };

  return (
    <Shell title={`Reschedule ${appointment.appointmentNo}`} onClose={onClose} busy={busy} wide footer={(
      <>
        <button type="button" className="apt-btn" onClick={onClose} disabled={busy}>Close</button>
        <button type="button" className="apt-btn apt-btn-primary" onClick={submit} disabled={busy}>{busy ? 'Saving…' : 'Reschedule'}</button>
      </>
    )}>
      <p className="apt-hint">Now: {formatDateLabel(appointment.appointmentDate)}, {slotLabel(appointment)}</p>
      <div className="apt-field" style={{ maxWidth: 220 }}>
        <label className="apt-label" htmlFor="apt-rs-date">New date</label>
        <input id="apt-rs-date" type="date" className="apt-input" min={todayIso()} value={date} onChange={(e) => { setDate(e.target.value); setSlot(''); }} />
      </div>
      <label className="apt-label" style={{ marginTop: 10 }}>New slot</label>
      <SlotPicker centreId={appointment.centreId} date={date} value={slot} onChange={setSlot} refreshKey={refresh}
        disabledSlot={sameDay ? appointment.slotStart : null} />
      <div className="apt-field" style={{ marginTop: 12 }}>
        <label className="apt-label" htmlFor="apt-rs-reason">Reason (optional)</label>
        <input id="apt-rs-reason" className="apt-input" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300} />
      </div>
      {error && <p className="apt-error-text" role="alert">{error}</p>}
    </Shell>
  );
};

export const CancelModal = ({ appointment, onClose, onDone }) => {
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);

  const submit = async () => {
    if (busyRef.current) return;
    if (!reason.trim()) { setError('A cancellation reason is required.'); return; }
    busyRef.current = true; setBusy(true); setError('');
    try {
      const res = await appointmentService.cancel(appointment.appointmentId, { reason: reason.trim(), expectedVersion: appointment.version });
      if (res.success) onDone(res.data); else setError(res.message || 'Could not cancel.');
    } catch (err) {
      setError(err.response?.data?.message || 'Could not cancel.');
    } finally { busyRef.current = false; setBusy(false); }
  };

  return (
    <Shell title={`Cancel ${appointment.appointmentNo}`} onClose={onClose} busy={busy} footer={(
      <>
        <button type="button" className="apt-btn" onClick={onClose} disabled={busy}>Keep appointment</button>
        <button type="button" className="apt-btn apt-btn-danger" onClick={submit} disabled={busy}>{busy ? 'Cancelling…' : 'Cancel appointment'}</button>
      </>
    )}>
      <p className="apt-hint">{appointment.pName} · {formatDateLabel(appointment.appointmentDate)} · {slotLabel(appointment)}. The slot is released.</p>
      <div className="apt-field">
        <label className="apt-label" htmlFor="apt-cancel-reason">Reason *</label>
        <textarea id="apt-cancel-reason" className="apt-input" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={300} autoFocus />
      </div>
      {error && <p className="apt-error-text" role="alert">{error}</p>}
    </Shell>
  );
};

